import React, { useContext, useEffect, useState } from "react";
import { Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import axios from "axios";
import MainNavbar from "./MainNavbar";
import Footer from "./Footer";
import OpportunityCompanion from "./home/OpportunityCompanion";
import DisclaimerDialog from "../../DisclaimerDialog";
import { AuthContext } from "../../../context/AuthContext";

const AppLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [providerDialogOpen, setProviderDialogOpen] = useState(false);
  const [showDivineInvite, setShowDivineInvite] = useState(true);
  const {
    isAuthenticated,
    profileUpdated,
    disclaimerAccepted,
    loading,
    setDisclaimerAccepted,
    logout,
  } = useContext(AuthContext);

  const isEnterprise = localStorage.getItem("isEnterprise") === "yes";
  const isProvider = localStorage.getItem("isprovider") === "yes";
  const isLoginPage = location.pathname === "/2.0/login";
  const isProfilePage = location.pathname === "/2.0/profile-update";
  const isMeetupRegistrationPage = /^\/2\.0\/community-meetup\/[^/]+\/register\/?$/.test(location.pathname);
  const isEventRegistrationPage = /^\/2\.0\/event\/[^/]+\/register\/?$/.test(location.pathname);
  const isCalendarEventDetailPage = location.pathname === "/2.0/calendar" && new URLSearchParams(location.search).has("event");
  const isRegistrationPage = isMeetupRegistrationPage || isEventRegistrationPage || isCalendarEventDetailPage;

  useEffect(() => {
    const promptSeen = sessionStorage.getItem("udyog2ProviderPromptSeen") === "yes";

    if (
      isAuthenticated &&
      !loading &&
      !isEnterprise &&
      isProvider &&
      disclaimerAccepted &&
      profileUpdated &&
      !promptSeen
    ) {
      setProviderDialogOpen(true);
      sessionStorage.setItem("udyog2ProviderPromptSeen", "yes");
    }
  }, [isAuthenticated, loading, isEnterprise, isProvider, disclaimerAccepted, profileUpdated]);

  useEffect(() => {
    let hideTimer;

    // Registration links must open directly to the form. Do not start the
    // promotional invite cycle while a visitor is trying to register.
    if (isLoginPage || isRegistrationPage) {
      setShowDivineInvite(false);
      return undefined;
    }

    const showForAWhile = () => {
      setShowDivineInvite(true);
      hideTimer = window.setTimeout(() => setShowDivineInvite(false), 12000);
    };

    setShowDivineInvite(false);
    const firstShowTimer = window.setTimeout(showForAWhile, 16000);
    const cycle = window.setInterval(showForAWhile, 46000);
    return () => {
      window.clearTimeout(firstShowTimer);
      window.clearInterval(cycle);
      window.clearTimeout(hideTimer);
    };
  }, [location.pathname, isLoginPage, isRegistrationPage]);

  const handleAgree = async () => {
    try {
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/disclaimer/accept`,
        {},
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      );
      setDisclaimerAccepted(true);
    } catch (error) {
      console.error("Error accepting disclaimer:", error);
    }
  };

  const handleLogout = () => {
    logout();
    window.location.href = "/2.0/login";
  };

  const openTutorialAssistant = () => {
    if (location.pathname === "/2.0" || location.pathname === "/2.0/") {
      window.dispatchEvent(new Event("udyog2-open-home-tour"));
      return;
    }

    navigate("/2.0", { state: { openTutorial: Date.now() } });
  };

  const openDivineRituals = () => {
    navigate("/uc/panditpurshothit");
  };

  if (isAuthenticated && loading) {
    return <div>Loading...</div>;
  }

  if (isAuthenticated && !isEnterprise && !isLoginPage && !disclaimerAccepted) {
    return <DisclaimerDialog open={true} onAgree={handleAgree} onLogout={handleLogout} />;
  }

  if (
    isAuthenticated &&
    !isEnterprise &&
    !isLoginPage &&
    !isProfilePage &&
    disclaimerAccepted &&
    !profileUpdated
  ) {
    return <Navigate to="/2.0/profile-update" replace state={{ from: location }} />;
  }

  return (
    <>
      <MainNavbar />
      <main style={{ padding: "30px" }}>
        <Outlet />
      </main>
      <div className="global-floating-rail" aria-label="Quick actions">
        <div className="divine-rituals-float-row">
          {showDivineInvite && !isLoginPage && !isRegistrationPage && (
            <div className="divine-rituals-invite" role="note">
              <button
                type="button"
                className="divine-rituals-invite-close"
                onClick={() => setShowDivineInvite(false)}
                aria-label="Dismiss Divine Rituals invitation"
                title="Dismiss invitation"
              >
                ×
              </button>
              <strong>Having trouble finding a Pandit?</strong>
              <span>Book your Puja or ritual at the right muhurta for proper blessings.</span>
            </div>
          )}
          <button
            type="button"
            className="global-floating-action divine-rituals-launch-fab"
            data-tour="divine-rituals-fab"
            onClick={openDivineRituals}
            aria-label="Open Divine Rituals pooja booking"
            title="Book pooja with Divine Rituals"
          >
            <span className="divine-rituals-om-mark" aria-hidden="true">ॐ</span>
            <span className="launch-copy">
              <strong>Divine Rituals</strong>
              <small>New pooja booking</small>
            </span>
            <span className="launch-badge">New</span>
          </button>
        </div>
        <button
          type="button"
          className="global-floating-action tutorial-assistant-fab"
          onClick={openTutorialAssistant}
          aria-label="Show tutorial assistant"
          title="Show tutorial"
        >
          <HelpOutlineIcon fontSize="small" />
          <span className="tutorial-copy">Tutorial</span>
        </button>
      </div>
      {!isRegistrationPage && <OpportunityCompanion />}
      <Dialog
        open={providerDialogOpen}
        onClose={() => setProviderDialogOpen(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: 4,
            p: 1,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 800 }}>
          Share Opportunities With the Community
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "grid", gap: 1 }}>
            <Typography variant="body1">
              We noticed you are registered as a provider.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              If you have job openings, mentoring support, business opportunities,
              or helpful leads, you can add them for Udyog Connect members to view.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={() => setProviderDialogOpen(false)} variant="outlined">
            Later
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setProviderDialogOpen(false);
              navigate("/2.0/opportunities");
            }}
          >
            Add Opportunities
          </Button>
        </DialogActions>
      </Dialog>
      <Footer />
    </>
  );
};

export default AppLayout;
